import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { light } from '~/constants/theme'
import useTranscriptionLogContext from '~/pages/CallAnalytics/Transcription/context/TranscriptionLogContext'
import { formatMilliseconds } from '~/utils/formatter'

const PauseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 14px 2px;
`

interface PauseProps {
  active: boolean
}

const inactivePauseBackgroundColor = light.gray300
const activeGradientKeyframes = keyframes`
  0% {
    outline-color: ${light.dark600};
    background-color: ${light.gray300};
  }
  50% {
    outline-color: ${light.dark800};
    background-color: ${light.gray500};
  }
  100% {
    outline-color: ${light.dark600};
    background-color: ${light.gray300};
  }
`
const Pause = styled.span<PauseProps>`
  display: flex;
  flex-direction: row;
  height: 21px;
  background-color: ${(props) =>
    props.active ? light.gray300 : inactivePauseBackgroundColor};
  background-size: ${(props) => (props.active ? '400% 400%' : '')};
  border-radius: 4px;
  outline: 2px dotted;
  outline-color: ${light.dark600};
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.active &&
    css`
      animation: ${activeGradientKeyframes} 2.25s linear infinite;
    `}
`

const textAnimation = keyframes`
  0% {
    color: ${light.dark700};
  }
  50% {
    color: ${light.dark800};
  }
  100% {
    color: ${light.dark700};
  }
`
const PauseText = styled.span<PauseProps>`
  font-family: 'Inter';
  font-size: 14px;
  color: ${light.dark700};
  font-weight: bold;
  ${(props) =>
    props.active &&
    css`
      animation: ${textAnimation} 2.25s linear infinite;
    `};
`

interface TranscriptLogPauseProps {
  pauseStartTimestampMillis?: number
  pauseEndTimestampMillis?: number
}

const TranscriptLogPause = ({
  pauseStartTimestampMillis,
  pauseEndTimestampMillis,
}: TranscriptLogPauseProps) => {
  const { isPlaying, elapsedTimeMillis } = useTranscriptionLogContext()

  const pauseDurationMilliseconds =
    (pauseEndTimestampMillis ?? 0) - (pauseStartTimestampMillis ?? 0)
  const renderPause = pauseDurationMilliseconds > 1000 * 15 //15 seconds

  const active =
    isPlaying &&
    renderPause &&
    elapsedTimeMillis > pauseStartTimestampMillis! &&
    elapsedTimeMillis < pauseEndTimestampMillis!

  return renderPause ? (
    <PauseWrapper>
      <Pause active={active}>
        <PauseText active={active}>
          Silence {formatMilliseconds(pauseDurationMilliseconds)}
        </PauseText>
      </Pause>
    </PauseWrapper>
  ) : null
}

export default TranscriptLogPause
